import { PC_KEYWORDS_EN_TOP, PC_KEYWORDS_EN_TOP_TOTAL } from "@/content/police-clearance-corpus/top.gen";
import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

import { PC_FAQ_CATEGORIES } from "@/content/police-clearance-corpus/faq-defs";

export const Route = createFileRoute("/en/police-clearance/faq/")({
  loader: async (): Promise<{ PC_FAQ_EN: { q: string; a: string }[]; PC_KEYWORDS_EN: string[] }> => {
    const [m0] = await Promise.all([
      import("@/content/police-clearance-corpus"),
    ]);
    return { PC_FAQ_EN: m0.PC_FAQ_EN_FULL, PC_KEYWORDS_EN: m0.PC_KEYWORDS_EN };
  },
  component: PcFaqHubEn,
  head: ({ loaderData }) => {
    const { PC_FAQ_EN = [], PC_KEYWORDS_EN = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    const title = `Thai Police Clearance FAQ — 100+ Questions for Expats (2026)`;
    const description = `Complete FAQ for Thailand Police Clearance Certificate (PCSC): fingerprinting, POA from abroad, MFA + Apostille 2026, per-country requirements for USCIS, UKVI, ImmiAccount, IRCC, Schengen.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/en/police-clearance/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/police-clearance/faq" }],
        keywords: PC_KEYWORDS_EN_TOP.slice(0, 80),
      }),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbSchema([
          { name: "Home", url: `${site}/en` },
          { name: "Police Clearance FAQ", url: `${site}/en/police-clearance/faq` },
        ])) },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(PC_FAQ_EN)) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          keywords: PC_KEYWORDS_EN_TOP.slice(0, 100).join(", "),
        }) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/police-clearance/faq")) },
      ],
    };
  },
});

function PcFaqHubEn() {
  const { PC_FAQ_EN, PC_KEYWORDS_EN } = Route.useLoaderData() as { PC_FAQ_EN: { q: string; a: string }[]; PC_KEYWORDS_EN: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <span>Thai Police Clearance FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          Thai Police Clearance FAQ — {PC_FAQ_EN.length}+ Questions (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          Everything expats need for a Royal Thai Police Clearance Certificate: PCSC filing at Building 24 Pathumwan,
          Power of Attorney from abroad, government fees, MFA legalization + Apostille (28 February 2027), and per-country visa requirements.
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/police-clearance/faq" className="text-primary underline">Thai version →</a>
        </div>

        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PC_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/police-clearance/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleEn} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {PC_FAQ_EN.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Need a full-service package?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Free consultation with our Notarial Services Attorneys — fingerprinting, PCSC filing, certified translation,
            MFA/Apostille, embassy legalization, DHL worldwide. Full cycle 10–15 working days.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Contact us →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">Popular topics ({PC_KEYWORDS_EN_TOP_TOTAL} queries)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {PC_KEYWORDS_EN.map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>
      
        <FaqSourceNote topic="police-clearance" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}